
import { Component, Vue, Watch } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import {
  SurveyResponseList,
  SurveyResponseFilters,
  SurveyResponseStats,
} from '@/collections/survey_responses';

const FEATURE_LABELS: Record<string, string> = {
  download_full_data: 'Full Data Export',
  dashboard_359: 'Slow Moving Products',
  dashboard_360: 'Dead Stock',
  by_category: 'Sales by Collection',
  stock_availability: 'Stock Availability',
  favorite_reports_count: 'Save Custom Report',
};

const CHART_COLORS = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4',
  '#795548', '#607D8B', '#E91E63', '#3F51B5',
];

@Component({})
export default class SurveyResponses extends Vue {
  public responses: SurveyResponseList = new SurveyResponseList();
  public stats: SurveyResponseStats | null = null;
  public isLoading: boolean = false;
  public isExporting: boolean = false;
  public error: string = '';

  public selectedFeature: string = '';
  public selectedAnswer: string = '';
  public dateRange: string[] = [];
  public dateMenu: boolean = false;
  public currentPage: number = 1;
  public pageSize: number = 50;

  private donutChart: any = null;
  private barChart: any = null;

  public featureOptions = [
    { text: 'All Features', value: '' },
    { text: 'Full Data Export', value: 'download_full_data' },
    { text: 'Slow Moving Products', value: 'dashboard_359' },
    { text: 'Dead Stock', value: 'dashboard_360' },
    { text: 'Sales by Collection', value: 'by_category' },
    { text: 'Stock Availability', value: 'stock_availability' },
    { text: 'Save Custom Report', value: 'favorite_reports_count' },
  ];

  public answerOptions = [
    { text: 'All Answers', value: '' },
    { text: 'Performance tracking', value: 'performance_tracking' },
    { text: 'Inventory optimization', value: 'inventory_optimization' },
    { text: 'Report sharing', value: 'report_sharing' },
    { text: 'Purchasing decisions', value: 'purchasing_decisions' },
    { text: 'Trend analysis', value: 'trend_analysis' },
    { text: 'Other', value: 'other' },
  ];

  public headers = [
    { text: 'Date', value: 'createdAt', sortable: false },
    { text: 'Merchant ID', value: 'merchant_id', sortable: false },
    { text: 'Feature', value: 'feature_code', sortable: false },
    { text: 'Answer', value: 'answer_label', sortable: false },
    { text: 'Free Text', value: 'free_text', sortable: false },
  ];

  public get dateRangeText(): string {
    if (this.dateRange.length === 2) {
      return `${this.dateRange[0]} ~ ${this.dateRange[1]}`;
    }
    if (this.dateRange.length === 1) {
      return this.dateRange[0];
    }
    return '';
  }

  public get responsesThisMonth(): number {
    if (!this.stats?.byDate) return 0;
    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return this.stats.byDate
      .filter((d) => d.date.startsWith(monthPrefix))
      .reduce((sum, d) => sum + d.count, 0);
  }

  public get topAnswer(): string {
    if (!this.stats?.byAnswer) return '—';
    const entries = Object.entries(this.stats.byAnswer);
    if (entries.length === 0) return '—';
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0];
  }

  public get featureCount(): number {
    if (!this.stats?.byFeature) return 0;
    return Object.keys(this.stats.byFeature).length;
  }

  public async created(): Promise<void> {
    await this.loadData();
  }

  public async loadData(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    try {
      const filters = this.buildFilters();
      await Promise.all([
        this.responses.fetch(this.currentPage, this.pageSize, filters),
        this.responses.fetchStats(filters).then((s) => (this.stats = s)),
      ]);
      this.$nextTick(() => this.renderCharts());
    } catch (err: any) {
      this.error = err.message || 'Failed to load survey data';
    } finally {
      this.isLoading = false;
    }
  }

  public async exportCsv(): Promise<void> {
    this.isExporting = true;
    try {
      const filters = this.buildFilters();
      const blob = await this.responses.exportCsv(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `survey-responses-${new Date().toISOString().slice(0, 10)}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      this.error = err.message || 'Failed to export CSV';
    } finally {
      this.isExporting = false;
    }
  }

  public formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  public featureLabel(code: string): string {
    return FEATURE_LABELS[code] || code;
  }

  public onDateRangeSelected(): void {
    if (this.dateRange.length === 2) {
      this.dateMenu = false;
    }
  }

  @Watch('selectedFeature')
  @Watch('selectedAnswer')
  @Watch('dateRange')
  public onFilterChange(): void {
    this.currentPage = 1;
    this.loadData();
  }

  @Watch('currentPage')
  public onPageChange(): void {
    this.loadData();
  }

  public beforeDestroy(): void {
    if (this.donutChart) this.donutChart.destroy();
    if (this.barChart) this.barChart.destroy();
  }

  private buildFilters(): SurveyResponseFilters {
    const filters: SurveyResponseFilters = {};
    if (this.selectedFeature) filters.feature_code = this.selectedFeature;
    if (this.selectedAnswer) filters.answer = this.selectedAnswer;
    if (this.dateRange.length >= 1) filters.from = this.dateRange[0];
    if (this.dateRange.length === 2) filters.to = this.dateRange[1];
    return filters;
  }

  private renderCharts(): void {
    this.renderDonutChart();
    this.renderBarChart();
  }

  private renderDonutChart(): void {
    const ctx = document.getElementById('answerDistributionChart');
    if (!(ctx instanceof HTMLCanvasElement) || !this.stats?.byAnswer) return;
    if (this.donutChart) this.donutChart.destroy();

    const labels = Object.keys(this.stats.byAnswer);
    const data = Object.values(this.stats.byAnswer);
    if (labels.length === 0) return;

    this.donutChart = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: CHART_COLORS.slice(0, labels.length),
            borderWidth: 3,
            borderColor: '#fff',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 16,
              usePointStyle: true,
              pointStyle: 'circle',
              font: { size: 12 },
              generateLabels: (chart: any) => {
                const datasets = chart.data.datasets;
                return datasets[0].data.map((val: any, i: number) => ({
                  text: `${chart.data.labels[i]}: ${val}`,
                  fillStyle: datasets[0].backgroundColor[i],
                  strokeStyle: datasets[0].backgroundColor[i],
                  index: i,
                }));
              },
            },
          },
        },
      },
    });
  }

  private renderBarChart(): void {
    const ctx = document.getElementById('responsesOverTimeChart');
    if (!(ctx instanceof HTMLCanvasElement) || !this.stats?.byDate) return;
    if (this.barChart) this.barChart.destroy();

    const byDate = this.stats.byDate;
    if (byDate.length === 0) return;

    this.barChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: byDate.map((d) => d.date),
        datasets: [
          {
            label: 'Responses',
            data: byDate.map((d) => d.count),
            backgroundColor: '#2196F3',
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            ticks: { stepSize: 1 },
          },
        },
      },
    });
  }
}
